<script setup>
import {ref, watch, computed} from 'vue'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import FormControlTypeRelationshipOneToMany from '@/common/form-control-type/FormControlTypeRelationshipOneToMany'

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
})

// Pinia Stores
const itemDataStore = useItemDataStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Local Reactive State ---
const courseType = ref(null)
const isLoading = ref(false)

// --- Methods ---
const toggleField = (name, show) => {
    const fields = formStore.value?.fields
    const field = fields?.find(o => o.columnName === name)

    if (field) {
        field.hidden = !show
    }

    if (!show) {
        if (name === 'schoolId' && props.currentData[name]) {
            props.currentData[name] = 0
        } else if (name === 'location' && props.currentData[name]) {
            props.currentData[name] = ''
        }
    }
}

const updateVisibility = (type) => {
    if (type === 'After school club') {
        toggleField('location', false)
        toggleField('schoolId', true)
    } else if (type) {
        toggleField('location', true)
        toggleField('schoolId', false)
    } else {
        // Only hide everything if courseType is explicitly null/false
        // (i.e., no course selected)
        toggleField('location', false)
        toggleField('schoolId', false)
    }
}

const fetchCourseType = async (courseId) => {
    if (!courseId) {
        courseType.value = null
        return
    }

    isLoading.value = true
    try {
        const obj = await itemDataStore.get({
            tableName: 'odp_courses',
            id: courseId,
        })
        courseType.value = obj?.courseType ?? null
    } finally {
        isLoading.value = false
    }
}

// --- Watchers ---

// Fetch courseType whenever courseId changes
watch(
    () => props.currentData?.courseId,
    (newId) => {
        if (!newId) {
            courseType.value = null
            updateVisibility(null)
        } else {
            fetchCourseType(newId)
        }
    },
    {immediate: true}
)

// Update field visibility whenever courseType resolves
watch(
    courseType,
    (newType) => {
        // 1. If we are currently loading, do nothing.
        // This prevents the initial 'null' from wiping data before the API responds.
        if (isLoading.value) return

        // 2. If we have a type, or if the ID is missing (newType is null), update.
        updateVisibility(newType)
    },
    {immediate: true}
)
</script>

<template>
    <FormControlTypeRelationshipOneToMany
        :current-data="currentData"
        :field="field"
        :form-id="formId"
    />
</template>